import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message, 
  Popconfirm,
  Card,
  Typography,
  Tag,
  Tooltip,
  Row,
  Col
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  UserOutlined,
  ReloadOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { giftService, GiftGiver, CreateGiftGiverRequest, UpdateGiftGiverRequest } from '../../services/giftService';
import styles from './GiftGiversPage.module.css';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const GiftGiversPage: React.FC = () => {
  const navigate = useNavigate();
  const [givers, setGivers] = useState<GiftGiver[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGiver, setEditingGiver] = useState<GiftGiver | null>(null);
  const [form] = Form.useForm();

  const fetchGivers = async () => {
    try {
      setLoading(true);
      const response = await giftService.getGiftGivers();
      setGivers(response.data);
    } catch (error) {
      message.error('Không thể tải danh sách người mừng');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGivers();
  }, []);

  const handleCreate = () => {
    setEditingGiver(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: GiftGiver) => {
    setEditingGiver(record);
    form.setFieldsValue({
      full_name: record.full_name,
      relationship: record.relationship,
      phone: record.phone,
      email: record.email,
      address: record.address,
      social_media: record.social_media,
      notes: record.notes
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await giftService.deleteGiftGiver(id);
      message.success('Xóa người mừng thành công');
      fetchGivers();
    } catch (error) {
      message.error('Không thể xóa người mừng');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingGiver) {
        await giftService.updateGiftGiver(editingGiver.id, values);
        message.success('Cập nhật người mừng thành công');
      } else {
        await giftService.createGiftGiver(values);
        message.success('Tạo người mừng thành công');
      }

      setModalVisible(false);
      fetchGivers();
    } catch (error) {
      message.error('Có lỗi xảy ra');
    }
  };

  const getRelationshipLabel = (relationship: string) => {
    const labels: Record<string, string> = {
      family_internal: 'Gia đình nội',
      family_external: 'Gia đình ngoại',
      friend_bride: 'Bạn cô dâu',
      friend_groom: 'Bạn chú rể',
      colleague: 'Đồng nghiệp',
      business_partner: 'Đối tác',
      other: 'Khác'
    };
    return labels[relationship] || relationship;
  };

  const getRelationshipColor = (relationship: string) => {
    const colors: Record<string, string> = {
      family_internal: 'red',
      family_external: 'orange',
      friend_bride: 'pink',
      friend_groom: 'blue',
      colleague: 'green',
      business_partner: 'purple',
      other: 'default'
    };
    return colors[relationship] || 'default';
  };

  const columns = [
    {
      title: 'Họ và tên',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text: string) => <strong>{text}</strong>
    },
    {
      title: 'Mối quan hệ',
      dataIndex: 'relationship',
      key: 'relationship',
      render: (relationship: string) => (
        <Tag color={getRelationshipColor(relationship)}>
          {getRelationshipLabel(relationship)}
        </Tag>
      )
    },
    {
      title: 'Thông tin liên hệ',
      key: 'contact',
      render: (_, record: GiftGiver) => (
        <Space direction="vertical" size="small">
          {record.phone && (
            <Space>
              <PhoneOutlined />
              {record.phone}
            </Space>
          )}
          {record.email && (
            <Space>
              <MailOutlined />
              {record.email}
            </Space>
          )}
          {record.address && (
            <Space>
              <HomeOutlined />
              <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {record.address}
              </span>
            </Space>
          )}
        </Space>
      )
    },
    {
      title: 'Mạng xã hội',
      dataIndex: 'social_media',
      key: 'social_media',
      render: (text: string) => text || '-'
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      render: (text: string) => text || '-',
      ellipsis: true
    },
    {
      title: 'Trạng thái',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Hoạt động' : 'Không hoạt động'}
        </Tag>
      )
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_, record: GiftGiver) => (
        <Space>
          <Tooltip title="Xem tiền mừng">
            <Button 
              type="link" 
              size="small"
              onClick={() => navigate(`/gifts?giver_id=${record.id}`)}
            >
              Xem tiền mừng
            </Button>
          </Tooltip>
          <Tooltip title="Chỉnh sửa">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Bạn có chắc muốn xóa người mừng này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Có"
            cancelText="Không"
          >
            <Tooltip title="Xóa">
              <Button 
                type="link" 
                danger 
                icon={<DeleteOutlined />} 
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className={styles.container}>
      <Card className={styles.header}>
        <div className={styles.headerContent}>
          <Title level={2} className={styles.title}>
            <UserOutlined /> Quản lý Người Mừng
          </Title>
          <Space>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchGivers}
              loading={loading}
            >
              Làm mới
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Thêm người mừng
            </Button>
          </Space>
        </div>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={givers}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} của ${total} người mừng`
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title={editingGiver ? 'Chỉnh sửa người mừng' : 'Thêm người mừng mới'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            relationship: 'other'
          }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="full_name"
                label="Họ và tên"
                rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
              >
                <Input placeholder="Nhập họ và tên đầy đủ" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="relationship"
                label="Mối quan hệ"
                rules={[{ required: true, message: 'Vui lòng chọn mối quan hệ' }]}
              >
                <Select placeholder="Chọn mối quan hệ">
                  <Option value="family_internal">Gia đình nội</Option>
                  <Option value="family_external">Gia đình ngoại</Option>
                  <Option value="friend_bride">Bạn cô dâu</Option>
                  <Option value="friend_groom">Bạn chú rể</Option>
                  <Option value="colleague">Đồng nghiệp</Option>
                  <Option value="business_partner">Đối tác</Option>
                  <Option value="other">Khác</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Số điện thoại"
              >
                <Input placeholder="Nhập số điện thoại" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
              >
                <Input placeholder="Nhập địa chỉ email" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="social_media"
                label="Mạng xã hội"
              >
                <Input placeholder="Facebook, Zalo, v.v." />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="Địa chỉ"
          >
            <TextArea 
              rows={2} 
              placeholder="Nhập địa chỉ chi tiết"
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Ghi chú"
          >
            <TextArea 
              rows={3} 
              placeholder="Ghi chú thêm về người mừng (tùy chọn)"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingGiver ? 'Cập nhật' : 'Thêm người mừng'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Hủy
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GiftGiversPage; 